import React, { useState } from 'react';
import { TouchableOpacity } from 'react-native';
import { Text, View } from '../Common/react-native-components';
import { isFunction } from 'lodash';
import { formatTwoDecimalsIfNeeded } from './utils';

const getComparatorFn = direction => {
  switch (direction) {
    case 'gt':
      return (a, b) => a != null && b !== null && a > b;
    case 'gte':
      return (a, b) => a != null && b !== null && a >= b;
    case 'lt':
      return (a, b) => a != null && b !== null && a < b;
    case 'lte':
      return (a, b) => a != null && b !== null && a <= b;
  }
};

function replacePlaceholders(template, values, row) {
  if (!template) return '--';

  const matches = [...template.matchAll(/\$(\d+)/g)];
  if (!matches.length) return template;

  const resolved = matches.map(m => {
    const idx = Number(m[1]);
    const key = Array.isArray(values) && values.length >= idx ? values[idx - 1] : undefined;
    return key != null ? row?.[key] : undefined;
  });

  if (resolved.some(v => v == null)) return '--';

  return template.replace(/\$(\d+)/g, (_, index) => {
    return String(resolved[index - 1]);
  });
}

const DataCell = ({ column, row, componentMap, setHoveredRowId, hoveredRowId }) => {
  const { type = 'text', threshold, templateConfig, rules, onPress } = column;

  const [hover, setHover] = useState(false);
  const rowHovered = hoveredRowId === row.id;
  const customSimpleRow = row.primary === 'Sample Size';
  const removeAllStyles = customSimpleRow;

  let displayValue = row[column.field] ?? '--';

  if (typeof templateConfig === 'object' && templateConfig !== null && !customSimpleRow) {
    displayValue = replacePlaceholders(templateConfig?.template, templateConfig?.keysToReplace, row);
  }

  let textColor = '#000';
  let backgroundColor = rowHovered ? '#f4f9ff' : 'transparent';
  const isSortable = column.sortable === true;

  const isBelowThreshold =
    type === 'threshold' && threshold && displayValue !== '--'
      ? getComparatorFn(threshold.direction)(row[threshold.field], threshold.value)
      : false;

  if (!removeAllStyles) {
    if (type === 'score' && Array.isArray(rules)) {
      const scoreValue = Number(displayValue);
      for (const rule of rules) {
        const compareFn = getComparatorFn(rule.direction);
        if (compareFn(scoreValue, rule.value)) {
          textColor = rule.color ?? textColor;
          backgroundColor = rule.backgroundColor ?? backgroundColor;
          break;
        }
      }
    }

    if (type === 'link') {
      textColor = '#266ec9';
    }
  }
  if (type === 'component' && isFunction(componentMap?.[column?.field]?.body)) {
    return componentMap[column.field].body({ column, row, isHeader: false });
  }
  return (
    <TouchableOpacity
      onHoverIn={() => {
        setHover(true);
        setHoveredRowId(row.id);
      }}
      onHoverOut={() => {
        setHover(false);
        setHoveredRowId(null);
      }}
      onPress={() => {
        if (type === 'link' && typeof onPress === 'function') {
          onPress(row);
        }
      }}
      disabled={type !== 'link'}
      style={{
        height: '100%',
        flex: 1,
        justifyContent: 'center',
        alignItems: isSortable ? 'flex-end' : 'flex-start',
        paddingHorizontal: 8,
        backgroundColor: removeAllStyles ? 'transparent' : backgroundColor
      }}
      activeOpacity={type === 'link' ? 0.3 : 1}>
      <View
        variant='body2'
        fontWeightType='medium'
        style={{
          justifyContent: 'center',
          alignItems: 'center',
          flexDirection: 'row',
          gap: isBelowThreshold ? 4 : 0,
          cursor: type === 'link' && !removeAllStyles ? 'pointer' : 'default'
        }}>
        {isBelowThreshold && !customSimpleRow && (
          <View
            style={{
              position: 'relative',
              width: 12,
              height: 12,
              borderRadius: '50%',
              border: '1.5px solid #D32F2F',
              overflow: 'hidden',
              flexShrink: 0
            }}>
            <View
              style={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                height: '50%',
                backgroundColor: '#D32F2F'
              }}
            />
          </View>
        )}
        <Text
          style={{
            color: removeAllStyles ? '#000' : isBelowThreshold ? '#d51b26' : textColor,
            textDecorationLine: type === 'link' && hover && !removeAllStyles ? 'underline' : 'none'
          }}>
          {formatTwoDecimalsIfNeeded(displayValue)}{' '}
        </Text>
      </View>
    </TouchableOpacity>
  );
};

export default DataCell;
